import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmacaoComponent } from '@components/dialog-confirmacao/dialog-confirmacao.component';

import { RegraConciliacaoAutomaticaApiService } from '../services/regra-conciliacao-automatica-api.service';
import { RegraConciliacaoAutomaticaResolverData } from '../models/regra-conciliacao-automatica-resolver-data';
import { RegraConciliacaoAutomatica } from '../models/regra-conciliacao-automatica';
import { RegraConciliacaoAutomaticaDialogComponent } from './dialogs/regra-conciliacao-automatica-dialog/regra-conciliacao-automatica-dialog.component';
import { TipoLancamentoOpcao } from '../models/tipo-lancamento-opcao';
import { TipoVerificacaoEnum, OPCOES_TIPO_VERIFICACAO } from '../models/tipo-verificacao.enum';
import { ContaCaixaConfiguracao } from '../../configuracao-caixa/models/conta-caixa-configuracao';
import { MessageService } from '@services/message.service';
import { PermissionService } from '@services/permission.service';
import { Permissao } from '@models/enums/permissao';

@Component({
  selector: 'app-regra-conciliacao-automatica',
  standalone: false,
  templateUrl: './regra-conciliacao-automatica.component.html',
  styleUrl: './regra-conciliacao-automatica.component.scss'
})
export class RegraConciliacaoAutomaticaComponent implements OnInit {

  protected get permissaoEditarRegra() { return Permissao.EDITAR_REGRA_CONCILIACAO_AUTOMATICA; }

  protected get novaRegraDesabilitada(): boolean {
    return !this.contaSelecionada || !this.permissionService.has(this.permissaoEditarRegra);
  }

  contas: ContaCaixaConfiguracao[] = [];
  tiposDestino: TipoLancamentoOpcao[] = [];

  contaSelecionada: ContaCaixaConfiguracao | null = null;
  regras: RegraConciliacaoAutomatica[] = [];
  tiposOrigem: TipoLancamentoOpcao[] = [];

  constructor(
    private route: ActivatedRoute,
    private regraConciliacaoAutomaticaApiService: RegraConciliacaoAutomaticaApiService,
    private messageService: MessageService,
    private permissionService: PermissionService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.route.data.subscribe((data: Data) => {
      const resolverData = data['resolverData'] as RegraConciliacaoAutomaticaResolverData;
      this.contas = resolverData.contas.filter(c => !!c.contaBancoId);
      this.tiposDestino = resolverData.tiposDestino;
    });
  }

  public onContaSelecionada(conta: ContaCaixaConfiguracao | null): void {
    this.contaSelecionada = conta;
    this.regras = [];
    this.tiposOrigem = (conta?.tipos ?? []).map(t => ({ id: t.id, nome: t.nome }));

    if (conta) this.carregarRegras(conta.id);
  }

  public novoClick(): void {
    this.abrirDialog(null);
  }

  public editarClick(item: RegraConciliacaoAutomatica): void {
    this.abrirDialog(item);
  }

  public removerClick(regra: RegraConciliacaoAutomatica): void {
    const dialogRef = this.dialog.open(DialogConfirmacaoComponent, {
      width: '467px',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.titulo = 'Remover regra';
    dialogRef.componentInstance.mensagem = `A regra do termo "${regra.termoBusca}" será removida.`;
    dialogRef.componentInstance.textoBtnConfirmacao = 'Remover';
    dialogRef.componentInstance.btnBackgroundColor = '#F44336';
    dialogRef.componentInstance.acaoConfirmacao = () => this.remover(regra);
  }

  public labelTipoVerificacao(tipoVerificacao: TipoVerificacaoEnum): string {
    return OPCOES_TIPO_VERIFICACAO.find(o => o.value === tipoVerificacao)?.label ?? '';
  }

  private abrirDialog(regra: RegraConciliacaoAutomatica | null): void {
    const dialogRef = this.dialog.open(RegraConciliacaoAutomaticaDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      panelClass: 'no-padding-dialog',
      disableClose: true,
    });
    dialogRef.componentInstance.contaCaixaId = this.contaSelecionada!.id;
    dialogRef.componentInstance.regra = regra;
    dialogRef.componentInstance.tiposOrigem = this.tiposOrigem;
    dialogRef.componentInstance.tiposDestino = this.tiposDestino;

    dialogRef.afterClosed().subscribe((resultado: RegraConciliacaoAutomatica | undefined) => {
      this.aplicarResultadoDialog(resultado ?? null);
    });
  }

  // Aplica o resultado do dialog de criação/edição à lista local: atualiza a regra editada
  // (mesmo id) ou adiciona a nova regra, sem precisar recarregar a lista inteira do servidor.
  private aplicarResultadoDialog(resultado: RegraConciliacaoAutomatica | null): void {
    if (!resultado) return;

    const index = this.regras.findIndex(r => r.id === resultado.id);
    if (index >= 0)
      this.regras = this.regras.map((r, i) => i === index ? resultado : r);
    else
      this.regras = [...this.regras, resultado];
  }

  private remover(regra: RegraConciliacaoAutomatica): void {
    this.regraConciliacaoAutomaticaApiService.deletar(regra.id).subscribe({
      next: () => {
        this.messageService.showToastSuccess('Regra removida com sucesso');
        this.regras = this.regras.filter(r => r.id !== regra.id);
      }
    });
  }

  private carregarRegras(contaCaixaId: number): void {
    this.regraConciliacaoAutomaticaApiService.listarPorContaCaixa(contaCaixaId).subscribe((regras: RegraConciliacaoAutomatica[]) => {
      this.regras = regras;
    });
  }
}
